import { type ProjectTemplateDocument, ProjectTemplateDocumentSchema } from '@servicetim/shared';
import { useImportTemplate } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  Textarea,
  toast,
} from '@servicetim/ui/components';
import { useId, useState } from 'react';
import { FormRow } from '../projects/ui/form-row';

export interface ImportTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Issue {
  path: string;
  message: string;
}

/** Parses and validates pasted/uploaded JSON with the shared document schema (client-side, before POST). */
export function validateTemplateJson(
  text: string,
): { document: ProjectTemplateDocument; issues: [] } | { document: null; issues: Issue[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return { document: null, issues: [{ path: '', message: `Not valid JSON: ${String(error)}` }] };
  }
  const result = ProjectTemplateDocumentSchema.safeParse(parsed);
  if (!result.success) {
    return {
      document: null,
      issues: result.error.issues.map((i) => ({
        path: i.path.map(String).join('.'),
        message: i.message,
      })),
    };
  }
  return { document: result.data, issues: [] };
}

/** Import a template from a JSON file or pasted text; validation issues list JSON paths. */
export function ImportTemplateDialog({ open, onOpenChange }: ImportTemplateDialogProps) {
  const id = useId();
  const importTemplate = useImportTemplate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [json, setJson] = useState('');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [nameError, setNameError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setJson('');
    setIssues([]);
    setNameError(null);
    importTemplate.reset();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    setJson(text);
    if (name.trim() === '') setName(file.name.replace(/\.json$/i, ''));
    setIssues(validateTemplateJson(text).issues);
  };

  const submit = async () => {
    setNameError(name.trim().length === 0 ? 'A name is required' : null);
    const result = validateTemplateJson(json);
    setIssues(result.issues);
    if (!result.document || name.trim().length === 0) return;
    try {
      await importTemplate.mutateAsync({
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
        document: result.document,
      });
      toast.success(`Template "${name.trim()}" imported`);
      reset();
      onOpenChange(false);
    } catch {
      // Shown by the banner below.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Import template</DialogTitle>
          <DialogDescription>
            Upload or paste a template document. It is validated against the template schema before
            it is sent.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {importTemplate.error ? <ProblemBanner error={importTemplate.error} size="sm" /> : null}
          <FormRow label="Name" htmlFor={`${id}-name`} required error={nameError}>
            <Input
              id={`${id}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              aria-invalid={nameError ? true : undefined}
            />
          </FormRow>
          <FormRow label="Description" htmlFor={`${id}-description`}>
            <Input
              id={`${id}-description`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </FormRow>
          <FormRow label="JSON file" htmlFor={`${id}-file`}>
            <Input
              id={`${id}-file`}
              type="file"
              accept="application/json,.json"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
          </FormRow>
          <FormRow
            label="Or paste JSON"
            htmlFor={`${id}-json`}
            hint={
              issues.length === 0 && json.trim().length > 0
                ? 'Looks valid.'
                : 'The document must have version 1 and portable refs only (no ids).'
            }
          >
            <Textarea
              id={`${id}-json`}
              value={json}
              onChange={(e) => {
                setJson(e.target.value);
                if (issues.length > 0) setIssues([]);
              }}
              onBlur={() => {
                if (json.trim().length > 0) setIssues(validateTemplateJson(json).issues);
              }}
              rows={10}
              className="font-mono text-xs"
              spellCheck={false}
              aria-invalid={issues.length > 0 || undefined}
            />
          </FormRow>
          {issues.length > 0 ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm"
            >
              <p className="font-medium text-red-900 dark:text-red-300">
                {issues.length} problem{issues.length === 1 ? '' : 's'} in the document
              </p>
              <ul className="mt-1 max-h-40 list-disc space-y-0.5 overflow-auto pl-5 text-red-800 dark:text-red-300 text-xs">
                {issues.slice(0, 50).map((i) => (
                  <li key={`${i.path}:${i.message}`}>
                    {i.path ? <code className="font-mono">{i.path}</code> : null} {i.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={importTemplate.isPending || json.trim().length === 0}>
              Import
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
